import { GameState, Position, IMove } from "../../../types";

/**
 * Basic move information used throughout the decision system
 */
export interface MoveInfo {
  fromPosition: Position;
  move: IMove;
}

/**
 * Context passed to each decision priority for analysis
 */
export interface DecisionContext {
  gameState: GameState;
  allPossibleMoves: MoveInfo[];
  currentTurn: number;
  timeRemaining?: number;
  debugMode?: boolean;
}

/**
 * Result returned by a decision priority analysis
 */
export interface DecisionResult {
  shouldEarlyExit: boolean;
  selectedMove?: MoveInfo;
  filteredMoves?: MoveInfo[];
  reasoning: string;
  priority: number;
  diagnostics?: {
    logs?: string[];
    [key: string]: any;
  };
}

/**
 * Base class for all decision priorities
 */
export abstract class BaseDecisionPriority {
  abstract readonly priority: number;
  abstract readonly name: string;
  abstract readonly description: string;

  /**
   * Quick check if this priority is relevant for the current position
   */
  abstract isRelevant(context: DecisionContext): boolean;

  /**
   * Analyze the position and return a decision
   */
  abstract analyze(context: DecisionContext): DecisionResult;

  /**
   * Helper method to create an early exit result
   */
  protected createEarlyExitResult(
    selectedMove: MoveInfo,
    reasoning: string,
    diagnostics?: any
  ): DecisionResult {
    return {
      shouldEarlyExit: true,
      selectedMove,
      reasoning,
      priority: this.priority,
      diagnostics,
    };
  }

  /**
   * Helper method to create a filter result
   */
  protected createFilterResult(
    filteredMoves: MoveInfo[],
    reasoning: string,
    diagnostics?: any
  ): DecisionResult {
    return {
      shouldEarlyExit: false,
      filteredMoves,
      reasoning,
      priority: this.priority,
      diagnostics,
    };
  }

  /**
   * Helper method to create a no-action result
   */
  protected createNoActionResult(reasoning: string): DecisionResult {
    return {
      shouldEarlyExit: false,
      reasoning,
      priority: this.priority,
    };
  }

  /**
   * Helper method for logging (can be overridden)
   */
  protected log(message: string, context?: DecisionContext): void {
    // Debug mode logging removed - use UI analytics instead
  }

  /**
   * Helper method to format positions for display
   */
  protected formatPosition(position: Position): string {
    return `[${position.row},${position.col}]`;
  }
}
